import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface TokenBalanceData {
  currentBalance: number;
  monthlyUsage: number;
  subscriptionTier: string;
}

interface TokenBalanceProps {
  refreshTrigger?: number; // Used to trigger refresh from parent
}

export const TokenBalance: React.FC<TokenBalanceProps> = ({ refreshTrigger }) => {
  const [balance, setBalance] = useState<TokenBalanceData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get('/tokens/balance');
      setBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch token balance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Refresh when triggered from parent
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchBalance();
    }
  }, [refreshTrigger, fetchBalance]);

  if (loading || !balance) {
    return null;
  }

  // Only show balance when 100 or less credits remaining
  if (balance.currentBalance > 100) {
    return null;
  }

  // Format balance with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <div className="flex items-center gap-2 px-3 py-1 bg-purple-900/20 rounded-lg border border-purple-800/30">
      <SparklesIcon className="w-4 h-4 text-purple-400" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-400">Balance</span>
        <span className="text-sm font-semibold text-purple-400">
          {formatNumber(balance.currentBalance)}
        </span>
      </div>
    </div>
  );
};
